import { useState, useEffect, useRef } from "react";
import { Sparkles, ShieldCheck, AlertTriangle, CheckCircle2, Send, Bot, Loader2, ArrowUpRight, Activity } from "lucide-react";
import { chatWithAdminAI, generateAdminExecutiveSummary, type AIAdminInsight, type AIMessage } from "@/project/lib/ai-engine";
import { toast } from "sonner";

export function AIAdminCopilot() {
  const [insight, setInsight] = useState<AIAdminInsight | null>(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState<AIMessage[]>([
    {
      role: "assistant",
      content: "Namaste Admin! Super Admin AI Copilot active. Ask about payout audits, onboarding risk, or donor retention.",
      timestamp: Date.now(),
    },
  ]);
  const [input, setInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const chatBottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    generateAdminExecutiveSummary().then((res) => {
      setInsight(res);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (chatOpen) {
      chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatOpen, messages]);

  async function handleSend(query?: string) {
    const text = (query ?? input).trim();
    if (!text || chatLoading) return;
    setInput("");
    setChatOpen(true);
    const userMsg: AIMessage = { role: "user", content: text, timestamp: Date.now() };
    const next = [...messages, userMsg];
    setMessages(next);
    setChatLoading(true);
    try {
      const reply = await chatWithAdminAI(next);
      setMessages((m) => [...m, { role: "assistant", content: reply, timestamp: Date.now() }]);
    } finally {
      setChatLoading(false);
    }
  }

  return (
    <div className="rounded-3xl border border-border/60 overflow-hidden shadow-xs bg-card">
      {/* Top Banner */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-6 bg-white border-b border-border/60">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--saffron)]/20 border border-[var(--saffron-deep)]/30 text-[var(--maroon-deep)]">
            <Sparkles className="h-5 w-5 text-[var(--saffron-deep)]" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-display text-xl font-bold text-[var(--maroon-deep)]">AI Admin Command Center</h2>
              <span className="rounded-full bg-[var(--saffron)]/20 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[var(--maroon-deep)] border border-[var(--saffron-deep)]/30">
                Super Admin AI
              </span>
            </div>
            <p className="mt-0.5 text-xs text-foreground/70">Real-time automated platform oversight, fraud detection & financial audits</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setChatOpen((v) => !v)}
            className="flex items-center gap-2 rounded-xl bg-[var(--maroon)] hover:bg-[var(--maroon-deep)] px-4 py-2 text-xs font-bold text-white shadow-xs transition"
          >
            <Bot className="h-4 w-4 text-[var(--saffron)]" />
            {chatOpen ? "Close AI Copilot" : "Open AI Copilot"}
          </button>
        </div>
      </div>

      {/* Main Body */}
      <div className="p-6 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-8 gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--maroon)]" />
            <span className="text-sm font-medium text-muted-foreground">Running system-wide AI audit...</span>
          </div>
        ) : insight ? (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Health & Compliance */}
            <div className="space-y-4">
              <div className="rounded-2xl border border-border bg-[var(--bg)] p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">AI System Health</span>
                  <Activity className="h-4 w-4 text-[var(--success)]" />
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-display text-3xl font-extrabold text-[var(--maroon-deep)]">{insight.healthScore}/100</span>
                  <span className="text-xs font-bold text-[var(--success)]">Optimal Performance</span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{insight.payoutAdvice}</p>
              </div>

              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4 dark:border-emerald-900/30 dark:bg-emerald-950/10">
                <div className="flex items-center justify-between text-emerald-700 dark:text-emerald-400">
                  <span className="text-xs font-bold uppercase tracking-wider">80G & 12A Compliance</span>
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <p className="mt-2 font-display text-2xl font-bold text-emerald-800 dark:text-emerald-300">{insight.complianceScore}%</p>
                <p className="mt-1 text-xs text-emerald-700/80 dark:text-emerald-400/80">100% of receipts verified against IT Form 10BE</p>
              </div>
            </div>

            {/* AI Urgent Alerts */}
            <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 dark:border-amber-900/30 dark:bg-amber-950/10 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 mb-3">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="text-xs font-bold uppercase tracking-wider">Active AI Alerts ({insight.alerts.length})</span>
                </div>
                <ul className="space-y-2.5">
                  {insight.alerts.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-amber-900/90 dark:text-amber-200 leading-relaxed">
                      <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => toast.info("AI Auto-Resolution: Prioritized water tanker grant for Banaskantha.")}
                className="mt-4 w-full rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold py-2 transition"
              >
                Auto-Resolve Priority Alerts
              </button>
            </div>

            {/* AI Action Recommendations */}
            <div className="rounded-2xl border border-border bg-[var(--bg)] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">AI Action Items</span>
                <Sparkles className="h-3.5 w-3.5 text-[var(--saffron-deep)]" />
              </div>
              <div className="space-y-2.5">
                {insight.recommendations.map((r, i) => (
                  <div key={i} className="rounded-xl border border-border/60 bg-white p-3 shadow-xs space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-[var(--maroon-deep)]">{r.title}</span>
                      <button
                        onClick={() => toast.success(`Executed: ${r.action}`)}
                        className="text-[10px] font-extrabold text-[var(--maroon)] hover:underline flex items-center gap-0.5"
                      >
                        Execute <ArrowUpRight className="h-3 w-3" />
                      </button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">{r.action}</p>
                    <p className="text-[10px] font-semibold text-[var(--success)]">Impact: {r.impact}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {/* AI Copilot Chat Drawer */}
        {chatOpen && (
          <div className="rounded-2xl border border-[var(--maroon)]/20 bg-white overflow-hidden shadow-md space-y-0">
            <div className="flex items-center justify-between px-4 py-3 bg-[var(--maroon)]/5 border-b border-border/60">
              <div className="flex items-center gap-2 text-xs font-bold text-[var(--maroon-deep)]">
                <Bot className="h-4 w-4 text-[var(--maroon)]" />
                <span>Super Admin AI Assistant</span>
              </div>
              <span className="text-[10px] text-muted-foreground">Ask anything about platform metrics, audits, or payouts</span>
            </div>

            <div className="max-h-60 overflow-y-auto p-4 space-y-3 bg-[var(--bg)]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && (
                    <div className="mr-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--maroon)]/10 mt-0.5">
                      <Bot className="h-3 w-3 text-[var(--maroon)]" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed ${
                      m.role === "user"
                        ? "bg-[var(--maroon)] text-white rounded-tr-sm"
                        : "bg-white text-foreground shadow-xs rounded-tl-sm border border-border/60"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--maroon)]" />
                  <span>AI Copilot analyzing admin data...</span>
                </div>
              )}
              <div ref={chatBottomRef} />
            </div>

            <div className="p-3 bg-white border-t border-border/60 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder="Ask about payouts, fraud risk, gaushala fodder levels..."
                className="flex-1 rounded-xl border border-border/60 bg-[var(--bg)] px-3.5 py-2 text-xs outline-none focus:ring-2 focus:ring-[var(--maroon)]/30"
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || chatLoading}
                className="rounded-xl bg-[var(--maroon)] text-white px-4 py-2 text-xs font-semibold hover:bg-[var(--maroon-deep)] transition disabled:opacity-40"
              >
                Send
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
